import { Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ArticleDetailsByOid } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleDetailsByOid';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ArticleService } from '../../article.service';

export interface ArticleDialogData {
	article: string;
	forCompany: boolean;
	onlyOrderableArticles?: boolean;
}

@Component({
	selector: 'app-article-select',
	templateUrl: './article-select.component.html',
	styleUrls: ['./article-select.component.scss']
})
export class ArticleSelectComponent implements OnDestroy {

	@Input() set forCompany(p_forCompany: boolean) {
		this.m_forCompany = p_forCompany;
	}

	get forCompany(): boolean {
		return this.m_forCompany;
	}

	@Input() set onlyOrderableArticles(p_onlyOrderAbleArticles: boolean) {
		this.m_onlyOrderableArticles = p_onlyOrderAbleArticles;
	}

	get onlyOrderableArticles(): boolean {
		return this.m_onlyOrderableArticles;
	}

	@Input() public displayName: string;

	@Input() set parentForm(p_form: UntypedFormGroup) {
		if (p_form != undefined) {
			this.m_parentForm = p_form;
			this.m_parentForm.get(this.formCtrlName).valueChanges.pipe(
				takeUntil(this.m_subscriptionsSubject)
			).subscribe({
				next: (res) => {
					if (this.getControl().value == '' || this.getControl().value == null) {
						this.displayName = '';
					}
				}
			});
		}
	}

	@Input() set formCtrlName(p_formField: string) {
		if (p_formField != undefined) {
			this.m_formCtrlName = p_formField;
		}
	}

	@Output() public articleChanged: EventEmitter<ArticleDetailsByOid.Result> = new EventEmitter();

	private m_forCompany: boolean = false;
	private m_onlyOrderableArticles: boolean = true;
	private m_selectedArticle: ArticleDetailsByOid.Result;
	private m_subscriptionsSubject = new Subject();
	public m_formCtrlName: string = 'article';
	private m_parentForm: UntypedFormGroup;

	constructor(
		private m_dialog: MatDialog,
		private m_formBuilder: UntypedFormBuilder,
		private m_articleService: ArticleService
	) {
		this.m_parentForm = this.m_formBuilder.group({
			article: []
		});
	}

	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	set SelectedArticle(p_selectedArticle: ArticleDetailsByOid.Result) {
		this.m_selectedArticle = p_selectedArticle;
		if (this.m_selectedArticle) {
			this.displayName = `${this.m_selectedArticle.id ? this.m_selectedArticle.id : ''} ${this.m_selectedArticle.descrLong ? this.m_selectedArticle.descrLong : ''}`;
		}
		if (this.m_parentForm) {
			this.getControl().setValue(this.m_selectedArticle ? this.m_selectedArticle.oid : '');
		}
		this.articleChanged.emit(this.m_selectedArticle);
	}

	get parentForm(): UntypedFormGroup {
		return this.m_parentForm;
	}

	get formCtrlName(): string {
		return this.m_formCtrlName;
	}

	get SelectedArticle(): ArticleDetailsByOid.Result {
		return this.m_selectedArticle;
	}

	/**
	 * Modaler Dialog wird aufgerufen. Ausgewählter Article wird zurückgegeben.
	 */
	public openDialog(): void {
		const dialogRef = this.m_dialog.open(ArticleDialog, {
			minWidth: '100%',
			maxWidth: '100%',
			minHeight: '100%',
			maxHeight: '100%',
			data: {
				article: this.m_selectedArticle,
				forCompany: this.forCompany,
				onlyOrderableArticles: this.m_onlyOrderableArticles
			}
		});

		dialogRef.afterClosed().pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (selectedArticleOid) => {
				if (selectedArticleOid && selectedArticleOid != '') {
					this.m_articleService.getArticleDetailsByOid(selectedArticleOid).pipe(
						takeUntil(this.m_subscriptionsSubject),
					).subscribe({
						next: (articleDetails) => {
							this.SelectedArticle = articleDetails;
						}
					});
				}
			}
		});
	}

	/**
	 * Setzt Inhalt des FormControl auf undefined.
	 */
	public clearInput_Click(): void {
		this.getControl().setValue('');
		this.articleChanged.emit(undefined);
	}

	/**
	 * Gibt das aktuelle FormControl zurück.
	 */
	public getControl(): AbstractControl {
		return this.m_parentForm.get(this.formCtrlName);
	}

	/**
	 * Prüft ob das aktuelle FormControl ein Pflichtfeld ist.
	 */
	public isRequiredField(): boolean {
		const form_field = this.getControl();
		if (!form_field.validator) {
			return false;
		}
		const validator = form_field.validator({} as AbstractControl);
		return (validator && validator.required);
	}
}

@Component({
	selector: 'article-select-dialog',
	templateUrl: 'article-select-dialog.html',
})
export class ArticleDialog {
	constructor(
		public m_dialogRef: MatDialogRef<ArticleDialog>,
		@Inject(MAT_DIALOG_DATA) public m_data: ArticleDialogData
	) { }

	/**
	 * Ein ausgewählter Article wird auf den Aufrufer des Dialoges zurückgegeben.
	 * @param p_articleOid Oid des ausgewählten Articles
	 */
	public selectArticle(p_articleOid: string): void {
		this.m_dialogRef.close(p_articleOid);
	}

	/**
	 * Der modale Dialog wird geschlossen.
	 */
	public closeDialog_Click(): void {
		this.m_dialogRef.close();
	}

}
